import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, ArrowLeft, ArrowRight, ImageIcon, Loader2, Wand2, Plus, CheckCircle2, ExternalLink, Plug, FileText, Smartphone, Square as SquareIcon, Film, RotateCcw } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Quick-pick categories that pre-fill the search box AND map to a Canva.com
// template-search deep-link. We don't get access to Canva's global template
// marketplace via the Connect API, but Canva's public URL (canva.com/templates/
// ?query=...) is the official browse experience — opening it in a new tab is
// the supported workflow: user picks a template there, Canva clones it into
// their account, they click Refresh in Nexso and import it.
const CANVA_CATEGORIES = [
  { label: "Menus", q: "menu" },
  { label: "Restaurant", q: "restaurant menu" },
  { label: "Drinks", q: "drink menu" },
  { label: "Coffee", q: "coffee shop menu" },
  { label: "Bar", q: "bar menu" },
  { label: "Pizza", q: "pizza menu" },
  { label: "Burgers", q: "burger menu" },
  { label: "Bakery", q: "bakery menu" },
  { label: "Promotions", q: "promotional sale" },
  { label: "Hours", q: "business hours sign" },
  { label: "Welcome", q: "welcome sign" },
  { label: "Specials", q: "today special" },
  { label: "Holiday", q: "holiday sale" },
  { label: "Event", q: "event poster" },
];

const openCanvaTemplateSearch = (query, orientationFilter) => {
  const q = (query || "").trim() || "digital signage";
  // Canva's search doesn't have a public orientation URL param, but the
  // ranking algorithm responds strongly to keywords — appending "portrait /
  // 9:16" or "landscape / 16:9" surfaces the right templates first.
  let extra = "";
  if (orientationFilter === "vertical")    extra = " portrait 9:16";
  else if (orientationFilter === "horizontal") extra = " landscape 16:9";
  const url = `https://www.canva.com/templates/?query=${encodeURIComponent(q + extra)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Layerre-powered banner studio. Lists templates the user imported on Layerre,
 * lets them edit text/image layers, generates a render, and offers a one-click
 * "Use as TV design" button that creates a new Nexso design with the banner
 * as the background image.
 */
export default function Banners() {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selected, setSelected] = useState(null); // template details with layers
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [overrides, setOverrides] = useState({}); // { layer_id: { text? or img_url? } }
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  // --- Canva Connect (OAuth) state ---
  const [canvaStatus, setCanvaStatus] = useState(null); // { configured, connected, profile? }
  const [canvaDesigns, setCanvaDesigns] = useState([]);
  const [loadingCanvaDesigns, setLoadingCanvaDesigns] = useState(false);
  const [canvaSearch, setCanvaSearch] = useState("");
  const [canvaContinuation, setCanvaContinuation] = useState(null);
  const [loadingMoreCanva, setLoadingMoreCanva] = useState(false);
  // --- "My Projects" tab state: imported designs already in Nexso ---
  const [activeTab, setActiveTab] = useState("library"); // "library" | "projects" | "ai"
  const [myProjects, setMyProjects] = useState([]);
  const [loadingMyProjects, setLoadingMyProjects] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // --- Orientation filter (TVs come in 16:9 landscape OR 9:16 portrait) ---
  const [orientation, setOrientation] = useState("all"); // "all" | "horizontal" | "vertical"
  // --- AI banner generator state ---
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiOrientation, setAiOrientation] = useState("horizontal"); // "horizontal" | "vertical" | "square"
  const [aiProvider, setAiProvider] = useState("gemini");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null); // { url, file_id }
  const [aiRefImage, setAiRefImage] = useState(null); // { file_id, url } - optional product photo
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const fetchTemplates = () => {
    setLoadingTemplates(true);
    return api.get("/banners/templates")
      .then((r) => setTemplates(Array.isArray(r.data) ? r.data : (r.data.templates || [])))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoadingTemplates(false));
  };

  const fetchCanvaStatus = async () => {
    try {
      const { data } = await api.get("/canva/status");
      setCanvaStatus(data);
      return data;
    } catch (err) {
      setCanvaStatus({ configured: false, connected: false });
      return null;
    }
  };

  const fetchCanvaDesigns = async ({ q, more } = {}) => {
    const isMore = !!more;
    if (isMore) setLoadingMoreCanva(true); else setLoadingCanvaDesigns(true);
    try {
      const params = {};
      const query = (q !== undefined ? q : canvaSearch).trim();
      if (query) params.query = query;
      if (isMore && canvaContinuation) params.continuation = canvaContinuation;
      const { data } = await api.get("/canva/designs", { params });
      const items = data?.items || data?.designs || [];
      setCanvaDesigns((prev) => isMore ? [...prev, ...items] : items);
      setCanvaContinuation(data?.continuation || null);
    } catch (err) {
      const msg = formatApiError(err);
      if (err?.response?.status === 401) {
        setCanvaStatus((s) => ({ ...(s || {}), connected: false, reason: "revoked" }));
        toast.error("Canva connection expired — please reconnect");
      } else {
        toast.error(msg);
      }
    } finally {
      if (isMore) setLoadingMoreCanva(false); else setLoadingCanvaDesigns(false);
    }
  };

  const fetchMyProjects = async () => {
    setLoadingMyProjects(true);
    try {
      const { data } = await api.get("/designs/by-source/canva");
      setMyProjects(data?.designs || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingMyProjects(false);
    }
  };

  // --- Curated Canva templates (admin-curated, public to all logged-in users)
  const [curated, setCurated] = useState([]);
  const [loadingCurated, setLoadingCurated] = useState(false);
  const fetchCurated = async ({ category, query, orient } = {}) => {
    setLoadingCurated(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (query) params.query = query;
      const o = orient ?? orientation;
      if (o && o !== "all") params.orientation = o;
      const { data } = await api.get("/canva/curated", { params });
      setCurated(data?.items || []);
    } catch (err) {
      // Silent fail — curated grid is non-critical, page still works without it.
      setCurated([]);
    } finally {
      setLoadingCurated(false);
    }
  };

  /** Clone an admin-curated Canva template directly into the customer's
   * Nexso projects — they never leave Nexso. Opens the canvas editor. */
  const cloneCuratedTemplate = async (t) => {
    const tId = toast.loading(`Cloning "${t.name}" into your projects…`);
    try {
      const { data } = await api.post(`/canva/curated/${t.id}/use`);
      toast.success(`"${data.name}" ready to edit`, { id: tId });
      if (data?.id) navigate(`/dashboard/designs/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load template", { id: tId });
    }
  };

  /** Silently pull NEW Canva designs into Nexso. Idempotent — designs already
   * imported (matched by canva_source.design_id) are skipped. */
  const syncCanva = async ({ silent = true } = {}) => {
    setSyncing(true);
    try {
      const { data } = await api.post("/canva/sync");
      if (data.imported > 0) {
        toast.success(`${data.imported} new Canva design${data.imported > 1 ? "s" : ""} synced into My Projects`);
        await fetchMyProjects();
      } else if (!silent) {
        toast.success("Up to date · no new Canva designs");
      }
    } catch (err) {
      if (!silent) toast.error(formatApiError(err) || "Canva sync failed");
    } finally {
      setSyncing(false);
    }
  };

  /** Re-render an imported Canva design at native HD resolution. Curated
   * templates use the admin-rendered HD asset; sync'd designs are exported
   * live via the user's Canva token. */
  const [upgradingId, setUpgradingId] = useState(null);

  // ─── Start blank ──────────────────────────────────────────────────────────
  // Create an empty canvas in the user's preferred orientation and jump
  // straight into the Canva-style editor. The most-requested first step for
  // designers who don't want to start from a template.
  const [blankPickerOpen, setBlankPickerOpen] = useState(false);
  const [creatingBlank, setCreatingBlank] = useState(false);
  const createBlankDesign = async (orient) => {
    setCreatingBlank(true);
    const dims = orient === "vertical"
      ? { w: 1080, h: 1920 }
      : orient === "square"
        ? { w: 1080, h: 1080 }
        : { w: 1920, h: 1080 };
    try {
      const { data } = await api.post("/designs", {
        template_id: "blank",
        name: "Untitled design",
        config: {
          layout: "canva_import",
          background_color: "#FFFFFF",
          orientation: orient,
          canvas_width: dims.w,
          canvas_height: dims.h,
          elements: [],
        },
      });
      toast.success("Blank canvas ready · let's design");
      navigate(`/dashboard/designs/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not create blank design");
    } finally {
      setCreatingBlank(false);
      setBlankPickerOpen(false);
    }
  };
  const upgradeProjectToHd = async (designId) => {
    setUpgradingId(designId);
    const tId = toast.loading("Upgrading to HD…");
    try {
      const { data } = await api.post(`/designs/${designId}/upgrade-hd`);
      if (data.upgraded) {
        toast.success("HD upgrade ready", { id: tId });
        await fetchMyProjects();
      } else {
        toast.success("Already in HD", { id: tId });
      }
    } catch (err) {
      // 5xx from Canva/CF can return HTML — show a clear static fallback
      // rather than leaking the gateway error.
      const status = err?.response?.status;
      if (status === 502 || status === 504) {
        toast.error(
          "HD render not available yet. If this template was curated, ask an admin to render HD for it.",
          { id: tId, duration: 6000 },
        );
      } else {
        toast.error(formatApiError(err) || "Could not upgrade to HD", { id: tId });
      }
    } finally {
      setUpgradingId(null);
    }
  };

  // Force-reimport an existing Canva import as an MP4 video. Used when a
  // video design was previously imported as a still image (frozen first
  // frame). Canva sync is idempotent so a re-sync alone won't fix it —
  // this endpoint deletes the stale image-based design and re-runs the
  // import with is_video=True.
  const [reimportingId, setReimportingId] = useState(null);
  const reimportAsVideo = async (designId) => {
    setReimportingId(designId);
    const tId = toast.loading("Re-importing as video from Canva… this can take 30–60 s");
    try {
      await api.post(`/designs/${designId}/reimport-as-video`);
      toast.success("Video import ready · open the banner to preview", { id: tId });
      await fetchMyProjects();
    } catch (err) {
      toast.error(formatApiError(err) || "Video re-import failed", { id: tId });
    } finally {
      setReimportingId(null);
    }
  };

  // Direct MP4 upload — accepts a user-provided video file (MP4/WebM/MOV,
  // ≤50 MB) and creates a Nexso design with a full-canvas video element.
  // Bypasses Canva entirely for customers who already have their promo cut.
  const videoInputRef = useRef(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so uploading the same file twice works
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`Video too large (${Math.round(file.size / (1024*1024))} MB). Max is 50 MB — please compress and try again.`);
      return;
    }
    setUploadingVideo(true);
    const tId = toast.loading(`Uploading ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("video", file);
      fd.append("name", file.name.replace(/\.[^.]+$/, ""));
      const { data } = await api.post("/designs/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Video banner ready", { id: tId });
      await fetchMyProjects();
      // Jump straight into the editor so the user can add text overlays etc.
      navigate(`/dashboard/designs/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Video upload failed", { id: tId });
    } finally {
      setUploadingVideo(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);
  useEffect(() => {
    fetchCanvaStatus().then(async (s) => {
      if (s?.connected) {
        // 1) Auto-sync to pull NEW designs from Canva first.
        await syncCanva({ silent: true });
        // 2) Then load both panels in parallel.
        await Promise.all([fetchCanvaDesigns(), fetchMyProjects()]);
      }
    });
    // Curated library is independent of Canva connection — load it always.
    fetchCurated();
    // Background re-sync every 60s while the page is open.
    const interval = setInterval(async () => {
      if (canvaStatus?.connected) await syncCanva({ silent: true });
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Handle return from OAuth callback (?canva=connected / ?canva=error&msg=…)
  useEffect(() => {
    const c = search.get("canva");
    if (!c) return;
    if (c === "connected") {
      toast.success("Canva connected — syncing your designs…");
      fetchCanvaStatus().then(async (s) => {
        if (s?.connected) {
          await syncCanva({ silent: true });
          await Promise.all([fetchCanvaDesigns(), fetchMyProjects()]);
        }
      });
    } else if (c === "error") {
      const m = search.get("msg") || "";
      toast.error(`Canva connect failed${m ? ` · ${m}` : ""}`);
    }
    // Strip the query so a refresh doesn't re-fire the toast.
    navigate("/dashboard/banners", { replace: true });
  }, []);

  const connectCanva = () => {
    // Full-page navigate so auth cookies flow with the request and the 302 to
    // canva.com can take over the browser cleanly.
    window.location.href = `${BACKEND}/api/canva/oauth/start`;
  };

  const disconnectCanva = async () => {
    try {
      await api.post("/canva/disconnect");
      setCanvaStatus({ configured: true, connected: false });
      setCanvaDesigns([]);
      toast.success("Canva disconnected");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const importCanvaDesignById = async (designId, designName) => {
    // Direct Canva → Nexso import (no Layerre): backend fetches the design's
    // thumbnail via Connect API and creates a Nexso design with it as a
    // fully-editable canvas element. Then we navigate straight to the editor.
    const tId = toast.loading(`Importing "${designName || "design"}"…`);
    try {
      const { data } = await api.post(`/canva/designs/${designId}/import-to-design`);
      toast.success(`Imported · "${data?.name || designName || "Canva design"}"`, { id: tId });
      if (data?.id) navigate(`/dashboard/designs/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not import this design", { id: tId });
    }
  };

  const openTemplate = async (t) => {
    setLoadingDetails(true);
    setSelected(t);
    setPreview(null);
    setOverrides({});
    try {
      const { data } = await api.get(`/banners/templates/${t.id}`);
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingDetails(false);
    }
  };

  const updateOverride = (layerId, patch) => {
    setOverrides((o) => ({ ...o, [layerId]: { ...(o[layerId] || {}), ...patch } }));
  };

  const generate = async () => {
    if (!selected) return;
    setGenerating(true);
    setPreview(null);
    try {
      const ovList = Object.entries(overrides)
        .filter(([_, p]) => Object.keys(p).length > 0)
        .map(([layer_id, properties]) => ({ layer_id, properties }));
      const { data } = await api.post("/banners/generate", {
        template_id: selected.id,
        overrides: ovList,
        export_type: "png",
      });
      const url = data.banner?.image_url || data.layerre?.url;
      setPreview(url);
      toast.success("Banner generated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const useAsDesign = async () => {
    if (!preview) return;
    try {
      const { data } = await api.post("/designs", {
        name: `Banner · ${selected.name}`,
        template_id: "canva_image",
        config: {
          layout: "image",
          title: selected.name,
          background_color: selected.background_color || "#0a0a0a",
          text_color: "#fff",
          accent_color: "#FAFF00",
          background_image: preview,
          image_fit: "cover",
          image_scale: 1,
          sections: [],
        },
      });
      toast.success("Design created");
      navigate(`/dashboard/designs/${data.id || data._id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // -------- Template grid view --------
  if (!selected) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-10 text-slate-900">
          <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Banner Studio · Canva</div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
                Open & edit your{" "}
                <span style={accentFont} className="text-sky-600">Canva</span> designs.
              </h1>
              <p className="mt-3 text-sm text-slate-500 max-w-2xl">
                Connect Canva, browse millions of templates, and pull any design straight into Nexso — text, prices, and images stay editable for your TVs.
              </p>
            </div>
          </div>

          {/* Canva Connect (OAuth) card */}
          {canvaStatus?.configured && (
            <div
              className="mb-10 rounded-3xl p-6 md:p-7 border backdrop-blur-xl"
              style={{
                background: "linear-gradient(135deg, rgba(34,197,94,0.08), rgba(139,92,246,0.05))",
                borderColor: canvaStatus.connected ? "rgba(34,197,94,0.30)" : "rgba(255,255,255,0.10)",
                boxShadow: canvaStatus.connected ? "0 0 30px rgba(34,197,94,0.10)" : "none",
              }}
              data-testid="canva-connect-card"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0 flex-1">
                  <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center shadow-[0_0_18px_rgba(139,92,246,0.5)]">
                    {canvaStatus.connected ? <CheckCircle2 className="h-5 w-5 text-slate-900" /> : <Plug className="h-5 w-5 text-slate-900" />}
                  </div>
                  <div className="min-w-0">
                    <Label className="text-[11px] uppercase tracking-[0.25em] text-sky-700 font-semibold">Canva account</Label>
                    {canvaStatus.connected ? (
                      <>
                        <div className="text-base font-semibold text-slate-900 mt-1" style={headingFont}>
                          Connected{canvaStatus.profile?.display_name ? ` · ${canvaStatus.profile.display_name}` : ""}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Your Canva designs appear below. Click any one to import it as an editable template.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="text-base font-semibold text-slate-900 mt-1" style={headingFont}>Connect your Canva account</div>
                        <p className="text-xs text-slate-500 mt-1 max-w-xl">
                          Sign in once with Canva to see all your designs inside Nexso. Read-only access · we can&apos;t modify anything in your Canva.
                        </p>
                        <p className="text-[11px] text-slate-400 mt-2 italic" data-testid="canva-account-note">
                          Note · You must have a Canva account to connect.
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {canvaStatus.connected ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => syncCanva({ silent: false })}
                        disabled={syncing}
                        className="rounded-full bg-slate-50 border-white/15 text-slate-900 hover:bg-slate-100 hover:text-slate-900"
                        data-testid="canva-refresh-button"
                      >
                        {syncing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing…</> : "Refresh"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={disconnectCanva}
                        className="rounded-full bg-slate-50 border-white/15 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                        data-testid="canva-disconnect-button"
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={connectCanva}
                      className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm font-semibold px-5"
                      data-testid="canva-connect-button"
                    >
                      <Plug className="h-4 w-4 mr-2" />
                      Connect Canva
                    </Button>
                  )}
                </div>
              </div>

              {/* Library + curated + (when connected) tabs.
                  The curated library is visible to ALL signed-in users —
                  Canva connection is only needed to import a design BACK
                  to Nexso for editing. */}
              {canvaStatus.configured && (
                <div className="mt-6">
                  {/* Tabs · "Canva Library" (search & import) vs "My Projects"
                      (designs already imported into Nexso). My Projects only
                      shows when the user is connected. */}
                  <div className="flex items-center gap-1 border-b border-slate-200 mb-5" data-testid="banner-tabs">
                    <button
                      type="button"
                      onClick={() => setActiveTab("library")}
                      className={`px-4 h-10 text-sm font-medium tracking-tight border-b-2 -mb-px transition-colors ${
                        activeTab === "library"
                          ? "border-sky-500 text-slate-900"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid="tab-library"
                    >
                      Canva Library
                    </button>
                    {canvaStatus.connected && (
                    <button
                      type="button"
                      onClick={() => setActiveTab("projects")}
                      className={`px-4 h-10 text-sm font-medium tracking-tight border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                        activeTab === "projects"
                          ? "border-sky-500 text-slate-900"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid="tab-projects"
                    >
                      My Projects
                      <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${
                        activeTab === "projects" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        {myProjects.length}
                      </span>
                    </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setActiveTab("ai")}
                      className={`px-4 h-10 text-sm font-medium tracking-tight border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                        activeTab === "ai"
                          ? "border-violet-500 text-slate-900"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid="tab-ai"
                    >
                      <Sparkles className={`h-4 w-4 ${activeTab === "ai" ? "text-violet-500" : "text-violet-400"}`} />
                      AI Generate
                      <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${
                        activeTab === "ai" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        NEW
                      </span>
                    </button>
                  </div>

                  {/* ─── AI Generate tab ────────────────────────────── */}
                  {activeTab === "ai" && (
                    <AIBannerTab
                      prompt={aiPrompt} setPrompt={setAiPrompt}
                      orientation={aiOrientation} setOrientation={setAiOrientation}
                      provider={aiProvider} setProvider={setAiProvider}
                      generating={aiGenerating} setGenerating={setAiGenerating}
                      result={aiResult} setResult={setAiResult}
                      refImage={aiRefImage} setRefImage={setAiRefImage}
                      onOpenAsDesign={async (imageUrl) => {
                        try {
                          const canvasW = aiOrientation === "vertical" ? 1080 : (aiOrientation === "square" ? 1080 : 1920);
                          const canvasH = aiOrientation === "vertical" ? 1920 : (aiOrientation === "square" ? 1080 : 1080);
                          const { data } = await api.post("/designs", {
                            name: `AI banner · ${aiPrompt.slice(0, 40) || "Untitled"}`,
                            template_id: "canva_image",
                            config: {
                              layout: "freeform",
                              canvas_w: canvasW,
                              canvas_h: canvasH,
                              background_color: "#FFFFFF",
                              elements: [
                                {
                                  id: "ai-bg-image",
                                  kind: "image",
                                  x: 0, y: 0, w: canvasW, h: canvasH,
                                  rotation: 0, z: 0, locked: false,
                                  src: imageUrl, fit: "cover", opacity: 1, borderRadius: 0,
                                },
                              ],
                            },
                          });
                          toast.success("Design created — opening editor…");
                          navigate(`/dashboard/designs/${data.id || data._id}`);
                        } catch (err) {
                          toast.error(formatApiError(err));
                        }
                      }}
                    />
                  )}

                  {/* ─── My Projects tab ─────────────────────────────── */}
                  {activeTab === "projects" && (
                    <div data-testid="my-projects-tab">
                      {loadingMyProjects ? (
                        <div className="text-slate-600 flex items-center gap-2 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" /> Loading your projects…
                        </div>
                      ) : myProjects.length === 0 ? (
                        <div className="rounded-2xl p-8 border-2 border-dashed border-slate-200 bg-slate-50/50 text-center">
                          <div className="text-sm font-semibold text-slate-900" style={headingFont}>No imported projects yet</div>
                          <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                            Switch to the <span className="font-semibold text-slate-700">Canva Library</span> tab and click <span className="font-semibold">Import</span> on any design — it&apos;ll appear here as a fully editable Nexso project.
                          </div>
                        </div>
                      ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-start" data-testid="my-projects-grid">
                          {myProjects.map((d) => {
                            const bg = (d.config?.elements || []).find((e) => e.id === "canva-bg");
                            const thumb = bg?.src ? `${BACKEND}${bg.src}` : "";
                            const editUrl = d.config?.canva_source?.edit_url;
                            const imgSrc = d.config?.canva_source?.image_source;
                            const isHd = imgSrc === "export";
                            const isUpgrading = upgradingId === d.id;
                            return (
                              <div
                                key={d.id}
                                className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:-translate-y-0.5 hover:border-sky-300 transition-all group"
                                data-testid={`my-project-${d.id}`}
                              >
                                <button
                                  onClick={() => navigate(`/dashboard/designs/${d.id}`)}
                                  className="aspect-video w-full overflow-hidden flex items-center justify-center bg-slate-100 relative"
                                >
                                  {thumb
                                    ? <img src={thumb} alt={d.name} className="w-full h-full object-contain" loading="lazy" />
                                    : <ImageIcon className="h-8 w-8 text-slate-300" />}
                                  <span
                                    className={`absolute top-2 right-2 rounded-full px-2 h-5 text-[10px] font-bold tracking-wide flex items-center ${
                                      isHd ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-800 border border-amber-300"
                                    }`}
                                    data-testid={`my-project-quality-${d.id}`}
                                  >
                                    {isHd ? "HD" : "SD"}
                                  </span>
                                </button>
                                <div className="p-4">
                                  <div className="text-sm font-semibold text-slate-900 truncate" title={d.name}>{d.name || "Untitled"}</div>
                                  <div className="text-[11px] text-slate-400 mt-0.5">
                                    {d.updated_at ? `Updated ${new Date(d.updated_at).toLocaleDateString()}` : "—"}
                                  </div>
                                  <div className="flex gap-2 mt-3">
                                    <Button
                                      size="sm"
                                      onClick={() => navigate(`/dashboard/designs/${d.id}`)}
                                      className="flex-1 rounded-full bg-sky-500 hover:bg-sky-600 text-white h-8 text-xs"
                                      data-testid={`my-project-open-${d.id}`}
                                    >
                                      Open
                                    </Button>
                                    {!isHd && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={isUpgrading}
                                        onClick={() => upgradeProjectToHd(d.id)}
                                        className="rounded-full bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 h-8 px-3 text-xs font-semibold"
                                        title="Re-export at native HD resolution from Canva"
                                        data-testid={`my-project-upgrade-${d.id}`}
                                      >
                                        {isUpgrading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Upgrade HD"}
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={reimportingId === d.id}
                                      onClick={() => reimportAsVideo(d.id)}
                                      className="rounded-full bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 h-8 px-2 text-xs font-semibold"
                                      title="If this is actually a video design and shows as a frozen frame, re-import it as an MP4"
                                      data-testid={`my-project-reimport-video-${d.id}`}
                                    >
                                      {reimportingId === d.id
                                        ? <Loader2 className="h-3 w-3 animate-spin" />
                                        : <Film className="h-3.5 w-3.5" />}
                                    </Button>
                                    {editUrl && (
                                      <a href={editUrl} target="_blank" rel="noreferrer">
                                        <Button size="sm" variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 h-8 px-2" title="Edit in Canva">
                                          <ExternalLink className="h-3.5 w-3.5" />
                                        </Button>
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ─── Canva Library tab ───────────────────────────── */}
                  {activeTab === "library" && (
                  <div data-testid="canva-library-tab">
                  {/* Quick-pick category chips — set the query + filter the user's
                      Canva library in one click. Each chip also pre-fills the
                      "Browse Canva.com" deep-link below the grid. */}
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="canva-categories">
                    {CANVA_CATEGORIES.map((c) => {
                      const active = canvaSearch.toLowerCase() === c.q.toLowerCase();
                      return (
                        <button
                          key={c.q}
                          type="button"
                          onClick={() => {
                            setCanvaSearch(c.q);
                            fetchCanvaDesigns({ q: c.q });
                            fetchCurated({ category: c.label.toLowerCase() });
                          }}
                          className={`rounded-full px-3.5 h-8 text-xs font-medium tracking-tight transition-all ${
                            active
                              ? "bg-slate-900 text-white shadow"
                              : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                          }`}
                          data-testid={`canva-category-${c.q.replace(/\s+/g, "-")}`}
                        >
                          {c.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Orientation filter — TVs come in landscape OR portrait. */}
                  <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="canva-orientation">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold mr-1">Screen</span>
                    {[
                      { id: "all", label: "All TVs", icon: "▢▯" },
                      { id: "horizontal", label: "Horizontal · 16:9", icon: "▭" },
                      { id: "vertical", label: "Vertical · 9:16", icon: "▮" },
                    ].map((o) => {
                      const active = orientation === o.id;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => {
                            setOrientation(o.id);
                            fetchCurated({ orient: o.id, query: canvaSearch });
                          }}
                          className={`rounded-full px-3.5 h-8 text-xs font-medium tracking-tight transition-all flex items-center gap-1.5 ${
                            active
                              ? "bg-sky-500 text-white shadow"
                              : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                          }`}
                          data-testid={`orientation-${o.id}`}
                        >
                          <span className="text-sm leading-none">{o.icon}</span>
                          {o.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Search bar — searches the user's own Canva library */}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      fetchCanvaDesigns({ q: canvaSearch });
                      fetchCurated({ query: canvaSearch });
                    }}
                    className="flex gap-2 mb-4"
                    data-testid="canva-search-form"
                  >
                    <Input
                      value={canvaSearch}
                      onChange={(e) => setCanvaSearch(e.target.value)}
                      placeholder="Search Canva… (e.g. restaurant menu, drink list, today special)"
                      className="flex-1 h-10 rounded-full bg-white border-slate-200 text-sm"
                      data-testid="canva-search-input"
                    />
                    <Button
                      type="submit"
                      disabled={loadingCanvaDesigns}
                      className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm h-10 px-5"
                      data-testid="canva-search-submit"
                    >
                      {loadingCanvaDesigns ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                    </Button>
                    {canvaSearch && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setCanvaSearch("");
                          fetchCanvaDesigns({ q: "" });
                          fetchCurated();
                        }}
                        className="rounded-full bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 text-sm h-10"
                        data-testid="canva-search-clear"
                      >
                        Clear
                      </Button>
                    )}
                  </form>

                  {/* ─── Curated Canva templates (admin-curated, all users see) ─── */}
                  <div className="mb-8" data-testid="canva-curated-section">
                    <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-violet-600 font-semibold">Featured templates</div>
                        <div className="text-sm font-semibold text-slate-900 mt-0.5" style={headingFont}>
                          {canvaSearch ? <>Matching &quot;{canvaSearch}&quot;</> : "Hand-picked Canva designs · ready to use"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => setBlankPickerOpen(true)}
                          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs h-8 shadow-sm"
                          data-testid="start-blank-btn"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Start blank
                        </Button>
                        <Button
                          onClick={() => videoInputRef.current?.click()}
                          disabled={uploadingVideo}
                          className="rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs h-8 shadow-sm"
                          title="Upload an MP4, WebM or MOV file (max 50 MB)"
                          data-testid="upload-video-btn"
                        >
                          {uploadingVideo
                            ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            : <Film className="h-3.5 w-3.5 mr-1" />}
                          Upload video
                        </Button>
                        <input
                          ref={videoInputRef}
                          type="file"
                          accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                          className="hidden"
                          onChange={handleVideoUpload}
                          data-testid="upload-video-input"
                        />
                        <Button
                          variant="outline"
                          onClick={() => openCanvaTemplateSearch(canvaSearch, orientation)}
                          className="rounded-full bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100 text-xs h-8"
                          data-testid="curated-browse-more"
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          Browse 10,000+ more on Canva
                        </Button>
                      </div>
                    </div>
                    {loadingCurated ? (
                      <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading templates…</div>
                    ) : curated.length === 0 ? (
                      <div className="rounded-2xl p-6 border-2 border-dashed border-violet-200 bg-violet-50/30 text-center">
                        <Sparkles className="h-6 w-6 text-violet-500 mx-auto mb-2" />
                        <div className="text-sm font-semibold text-slate-900">No featured templates {canvaSearch ? "for this search" : "yet"}</div>
                        <div className="text-xs text-slate-600 mt-1 max-w-md mx-auto mb-3">
                          Click <span className="font-semibold text-violet-700">Browse 10,000+ more on Canva</span> above to find a template — Canva will clone it into your account, then auto-sync brings it to <span className="font-semibold">My Projects</span>.
                        </div>
                        <button
                          onClick={() => setBlankPickerOpen(true)}
                          className="text-xs text-slate-700 hover:text-slate-900 underline underline-offset-2"
                          data-testid="start-blank-link"
                        >
                          or, start from a blank canvas →
                        </button>
                      </div>
                    ) : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-start" data-testid="curated-grid">
                        {curated.map((t) => {
                          const aspect = t.orientation === "vertical"
                            ? "aspect-[9/16]"
                            : t.orientation === "square"
                              ? "aspect-square"
                              : "aspect-video";
                          return (
                          <div
                            key={t.id}
                            className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md transition-all"
                            data-testid={`curated-${t.id}`}
                          >
                            <button
                              type="button"
                              onClick={() => cloneCuratedTemplate(t)}
                              className={`${aspect} w-full block overflow-hidden bg-slate-100 group relative text-left`}
                              data-testid={`use-curated-thumb-${t.id}`}
                            >
                              {t.thumbnail_url
                                ? <img src={t.thumbnail_url} alt={t.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
                                : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="h-8 w-8 text-slate-300" /></div>}
                              {t.orientation && (
                                <span className="absolute top-2 right-2 text-[9px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 bg-white/95 text-slate-700 border border-slate-200 shadow-sm">
                                  {t.orientation === "vertical" ? "9:16" : t.orientation === "horizontal" ? "16:9" : "1:1"}
                                </span>
                              )}
                              <div className="absolute inset-0 bg-violet-900/0 group-hover:bg-violet-900/20 transition-colors flex items-center justify-center">
                                <span className="opacity-0 group-hover:opacity-100 rounded-full px-3 py-1.5 bg-violet-600 text-white text-xs font-semibold shadow-lg transition-opacity">
                                  Use this template →
                                </span>
                              </div>
                            </button>
                            <div className="p-3">
                              <div className="text-xs font-semibold text-slate-900 truncate" title={t.name}>{t.name}</div>
                              <div className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider">{t.category}</div>
                              <Button
                                size="sm"
                                onClick={() => cloneCuratedTemplate(t)}
                                className="w-full mt-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white h-8 text-xs font-semibold"
                                data-testid={`use-curated-${t.id}`}
                              >
                                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                                Use this template
                              </Button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Section divider before "your account" designs */}
                  {canvaStatus.connected && curated.length > 0 && (
                    <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-semibold mb-3 mt-4" data-testid="canva-mydesigns-heading">
                      Your Canva account
                    </div>
                  )}

                  {/* User's own Canva designs (only when connected) */}
                  {canvaStatus.connected && (
                  <>
                  {loadingCanvaDesigns ? (
                    <div className="text-slate-600 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading your Canva designs…</div>
                  ) : canvaDesigns.length === 0 ? (
                    <div className="space-y-5">
                      <div className="text-slate-500 text-sm">
                        {canvaSearch ? <>No designs in your Canva account match <span className="font-semibold text-slate-700">&quot;{canvaSearch}&quot;</span>.</> : "No designs found in your Canva account."}
                      </div>
                      {/* Same CTA so users in the empty state can still browse */}
                      <div className="rounded-2xl p-5 border-2 border-dashed border-violet-200 bg-gradient-to-br from-violet-50 to-pink-50 flex flex-col sm:flex-row items-center gap-4" data-testid="canva-browse-cta-empty">
                        <div className="h-12 w-12 shrink-0 rounded-xl bg-violet-600 flex items-center justify-center text-white shadow-lg">
                          <Sparkles className="h-6 w-6" />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                          <div className="text-sm font-semibold text-slate-900" style={headingFont}>
                            Browse millions of {canvaSearch ? `"${canvaSearch}" ` : ""}templates on Canva
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                            Pick any template on Canva → it clones to your account → come back and click <span className="font-semibold">Refresh</span> to import.
                          </div>
                        </div>
                        <Button
                          onClick={() => openCanvaTemplateSearch(canvaSearch, orientation)}
                          className="rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold h-10 px-5 shadow-md shrink-0"
                          data-testid="canva-browse-button-empty"
                        >
                          <ExternalLink className="h-4 w-4 mr-1.5" />
                          Browse Canva.com
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-start" data-testid="canva-designs-grid">
                      {canvaDesigns
                        .filter((d) => {
                          // Hide truly-empty designs (no title AND no thumbnail) — they
                          // clutter the library and can't be meaningfully imported.
                          const t = d.title || d.name;
                          const thumb = d.thumbnail?.url || d.thumbnail_url;
                          return !!t || !!thumb;
                        })
                        .filter((d) => {
                          if (orientation === "all") return true;
                          const w = d.thumbnail?.width || d.dimensions?.width || d.width;
                          const h = d.thumbnail?.height || d.dimensions?.height || d.height;
                          if (!w || !h) return true; // unknown → don't filter out
                          if (orientation === "vertical")   return h > w * 1.15;
                          if (orientation === "horizontal") return w > h * 1.15;
                          if (orientation === "square")     return Math.abs(w - h) / Math.max(w, h) < 0.15;
                          return true;
                        })
                        .map((d) => {
                        const thumb = d.thumbnail?.url || d.thumbnail_url || null;
                        const editUrl = d.urls?.edit_url || d.editUrl || d.edit_url;
                        const w = d.thumbnail?.width || d.dimensions?.width || d.width;
                        const h = d.thumbnail?.height || d.dimensions?.height || d.height;
                        const ori = !w || !h ? null
                          : (h > w * 1.15 ? "vertical" : (w > h * 1.15 ? "horizontal" : "square"));
                        const aspect = ori === "vertical" ? "aspect-[9/16]"
                          : ori === "square" ? "aspect-square" : "aspect-video";
                        return (
                          <div
                            key={d.id}
                            className="rounded-2xl border overflow-hidden backdrop-blur-md"
                            style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }}
                            data-testid={`canva-design-${d.id}`}
                          >
                            <div className={`${aspect} bg-slate-50 overflow-hidden relative`}>
                              {thumb ? (
                                <img src={thumb} alt={d.title || d.name || "Canva design"} className="w-full h-full object-contain" loading="lazy" decoding="async" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">No preview</div>
                              )}
                              {ori && (
                                <span className="absolute top-2 right-2 text-[9px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 bg-white/95 text-slate-700 border border-slate-200 shadow-sm">
                                  {ori === "vertical" ? "9:16" : ori === "horizontal" ? "16:9" : "1:1"}
                                </span>
                              )}
                            </div>
                            <div className="p-3 space-y-2">
                              <div className="text-sm font-semibold text-slate-900 truncate" style={headingFont}>{d.title || d.name || "Untitled"}</div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => importCanvaDesignById(d.id, d.title || d.name)}
                                  className="flex-1 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-xs h-8"
                                  data-testid={`canva-design-import-${d.id}`}
                                >
                                  <Plus className="h-3.5 w-3.5 mr-1" />Import
                                </Button>
                                {editUrl && (
                                  <a href={editUrl} target="_blank" rel="noopener noreferrer">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="rounded-full bg-slate-50 border-white/15 text-slate-900 hover:bg-slate-100 hover:text-slate-900 text-xs h-8"
                                      data-testid={`canva-design-open-${d.id}`}
                                    >
                                      <ExternalLink className="h-3.5 w-3.5" />
                                    </Button>
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {canvaContinuation && (
                      <div className="flex justify-center mt-6">
                        <Button
                          variant="outline"
                          onClick={() => fetchCanvaDesigns({ more: true })}
                          disabled={loadingMoreCanva}
                          className="rounded-full bg-white border-slate-200 text-slate-900 hover:bg-slate-50 text-sm h-10 px-6"
                          data-testid="canva-load-more"
                        >
                          {loadingMoreCanva ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading…</> : "Load more designs"}
                        </Button>
                      </div>
                    )}

                    {/* Deep-link to Canva.com's full template marketplace.
                        Canva's Connect API doesn't expose the global template
                        library, so this opens canva.com/templates/?query=… in
                        a new tab — the official, supported workflow. After the
                        user clones a template there, they hit Refresh below
                        and import it. */}
                    <div className="mt-8 rounded-2xl p-5 border-2 border-dashed border-violet-200 bg-gradient-to-br from-violet-50 to-pink-50 flex flex-col sm:flex-row items-center gap-4" data-testid="canva-browse-cta">
                      <div className="h-12 w-12 shrink-0 rounded-xl bg-violet-600 flex items-center justify-center text-white shadow-lg">
                        <Sparkles className="h-6 w-6" />
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <div className="text-sm font-semibold text-slate-900" style={headingFont}>
                          Need more templates? Browse millions on Canva.com
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                          {canvaSearch
                            ? <>We&apos;ll search Canva for <span className="font-semibold text-violet-700">&quot;{canvaSearch}&quot;</span> in a new tab. Pick a template → Canva clones it to your account → come back and click <span className="font-semibold">Refresh</span> to import.</>
                            : <>Type a keyword or pick a category above, then open Canva to browse their full library. Clone any template → return here and click Refresh.</>
                          }
                        </div>
                      </div>
                      <Button
                        onClick={() => openCanvaTemplateSearch(canvaSearch, orientation)}
                        className="rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold h-10 px-5 shadow-md shrink-0"
                        data-testid="canva-browse-button"
                      >
                        <ExternalLink className="h-4 w-4 mr-1.5" />
                        Browse Canva.com
                      </Button>
                    </div>
                    </>
                  )}
                  </>
                  )}
                  </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* End of Canva-focused Banner Studio. The legacy Layerre templates
              grid was removed — users import via the Canva OAuth flow above. */}
          </div>

          {/* ─── Start blank dialog ───────────────────────────────────────
              Pick an orientation, then jump straight into the Canva-style
              editor with a clean white canvas. */}
          <Dialog open={blankPickerOpen} onOpenChange={(o) => { if (!o) setBlankPickerOpen(false); }}>
            <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg" data-testid="blank-orientation-dialog">
              <DialogHeader>
                <DialogTitle style={headingFont}>Pick canvas orientation</DialogTitle>
                <DialogDescription className="text-slate-500">
                  This is just the starting shape — you can resize anything later inside the editor.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-3 mt-2" data-testid="blank-orientation-grid">
                {[
                  { v: "horizontal", icon: FileText,    title: "Horizontal", sub: "16:9 · 1920×1080", className: "h-16 w-24" },
                  { v: "vertical",   icon: Smartphone,  title: "Vertical",   sub: "9:16 · 1080×1920", className: "h-24 w-16" },
                  { v: "square",     icon: SquareIcon,  title: "Square",     sub: "1:1 · 1080×1080", className: "h-20 w-20" },
                ].map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      disabled={creatingBlank}
                      onClick={() => createBlankDesign(opt.v)}
                      className="group flex flex-col items-center justify-center p-5 rounded-2xl border-2 border-slate-200 bg-slate-50 hover:border-sky-400 hover:bg-sky-50 disabled:opacity-50 transition-all"
                      data-testid={`blank-orientation-${opt.v}`}
                    >
                      <div className={`flex items-center justify-center ${opt.className} mb-3 rounded-lg bg-white border-2 border-slate-300 group-hover:border-sky-400`}>
                        <Icon className="h-6 w-6 text-slate-400 group-hover:text-sky-600" strokeWidth={1.5} />
                      </div>
                      <div className="text-sm font-semibold text-slate-900">{opt.title}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBlankPickerOpen(false)} disabled={creatingBlank} className="rounded-full bg-slate-50 border-slate-200 text-slate-900">
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </DashboardLayout>
    );
  }

  // Dead-end fallback (no longer reachable — `selected` is never set).
  return (
    <DashboardLayout>
      <div className="p-10 text-slate-700 text-sm">Loading…</div>
    </DashboardLayout>
  );
}

// ============================================================================
// AI Banner Studio — the 3rd top-level tab.
//
// One-shot AI banner generation. Uses `/api/ai/image` (Gemini Nano Banana OR
// OpenAI GPT Image 1 via the Emergent LLM key). Supports optional reference
// image upload so users can generate banners featuring their actual product.
// ============================================================================
function AIBannerTab({
  prompt, setPrompt,
  orientation, setOrientation,
  provider, setProvider,
  generating, setGenerating,
  result, setResult,
  refImage, setRefImage,
  onOpenAsDesign,
}) {
  const inspirations = [
    { emoji: "☕", label: "Coffee shop grand opening banner, warm cream + espresso tones, hand-drawn steam" },
    { emoji: "🏋️", label: "New year fitness challenge banner, bold sans, neon-lime accents on charcoal" },
    { emoji: "🍕", label: "Two-for-Tuesday pizza deal banner, red + gold, checked tablecloth pattern" },
    { emoji: "💄", label: "Luxury salon rose-gold banner, glossy typography, minimalist" },
    { emoji: "🏠", label: "Real estate open house banner, sand-tan palette, elegant serif typography" },
    { emoji: "🛍️", label: "Weekend flash sale banner, black + yellow high-contrast, bold anton typography" },
  ];

  const generate = async () => {
    const p = (prompt || "").trim();
    if (!p) {
      toast.error("Describe the banner you want");
      return;
    }
    setGenerating(true);
    setResult(null);
    const tId = toast.loading("Generating banner… (≈15s)");
    try {
      // Compose a stronger prompt so we get true banner outputs, not generic photos.
      const aspect =
        orientation === "vertical" ? "vertical 9:16 aspect ratio"
        : orientation === "square" ? "square 1:1 aspect ratio"
        : "landscape 16:9 aspect ratio";
      const refBit = refImage?.url
        ? " The banner MUST prominently feature the exact product from the reference image (composited photorealistically, clean background)."
        : "";
      const finalPrompt = `Modern, high-quality digital signage banner design for a business. ${aspect}. Bold, editorial typography included in the artwork. Solid color palette. ${p}.${refBit}`;
      const r = await api.post("/ai/image", { prompt: finalPrompt, provider });
      setResult({ url: r.data.url, file_id: r.data.file_id });
      toast.success("Banner ready", { id: tId });
    } catch (err) {
      toast.error(formatApiError(err), { id: tId });
    } finally {
      setGenerating(false);
    }
  };

  const uploadRef = async (file) => {
    if (!file) return;
    const tId = toast.loading("Uploading reference image…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRefImage({ file_id: r.data.id, url: r.data.url });
      toast.success("Reference image ready", { id: tId });
    } catch (err) {
      toast.error(formatApiError(err), { id: tId });
    }
  };

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-6" data-testid="ai-banner-tab">
      {/* Left column — prompt + settings */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 h-fit sticky top-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4 text-violet-500" />
            <h3 className="text-sm font-bold text-slate-900" style={headingFont}>Describe your banner</h3>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">Tell the AI who you are, the offer, the vibe. Longer prompts = better banners.</p>
        </div>

        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g. Grand opening banner for a new artisan coffee shop, warm cream + espresso brown, hand-drawn steam over a latte, headline says 'Now Open'"
          className="w-full min-h-[130px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
          data-testid="ai-banner-prompt-input"
        />

        {/* Reference image (product) */}
        <div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Reference image (optional)</div>
          {refImage?.url ? (
            <div className="flex items-center gap-3 rounded-lg border border-violet-200 bg-violet-50 p-2" data-testid="ai-banner-ref-preview">
              <img src={refImage.url} alt="Reference" className="h-14 w-14 rounded-md object-cover shrink-0" />
              <div className="flex-1 min-w-0 text-xs text-slate-700">
                <div className="font-semibold text-slate-900 truncate">Product reference set</div>
                <div className="text-slate-500 text-[11px]">Nexso will build the banner around this product.</div>
              </div>
              <button
                onClick={() => setRefImage(null)}
                className="text-slate-400 hover:text-slate-700 shrink-0"
                data-testid="ai-banner-ref-remove"
              >
                <Trash2Icon />
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-3 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-violet-300 p-3 cursor-pointer transition-colors" data-testid="ai-banner-ref-uploader">
              <div className="h-10 w-10 rounded-lg bg-slate-200 flex items-center justify-center text-slate-500">
                <UploadIcon />
              </div>
              <div className="flex-1 text-xs">
                <div className="font-semibold text-slate-900">Upload product photo</div>
                <div className="text-slate-500 text-[11px]">The AI will build the banner around it.</div>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadRef(e.target.files?.[0])}
                data-testid="ai-banner-ref-file"
              />
            </label>
          )}
        </div>

        {/* Orientation */}
        <div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Orientation</div>
          <div className="grid grid-cols-3 gap-2" data-testid="ai-banner-orientation">
            {[
              { v: "horizontal", label: "16:9", hint: "Landscape" },
              { v: "square", label: "1:1", hint: "Square" },
              { v: "vertical", label: "9:16", hint: "Portrait" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setOrientation(o.v)}
                className={`rounded-lg border text-xs font-semibold py-2 transition-colors ${
                  orientation === o.v
                    ? "border-violet-400 bg-violet-50 text-violet-700"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`ai-banner-orient-${o.v}`}
              >
                <div>{o.label}</div>
                <div className="text-[10px] font-normal text-slate-500 mt-0.5">{o.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Provider */}
        <div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Model</div>
          <div className="grid grid-cols-2 gap-2" data-testid="ai-banner-provider">
            {[
              { v: "gemini", label: "Nano Banana", hint: "Fast · free" },
              { v: "openai", label: "GPT Image 1", hint: "Premium · $0.05" },
            ].map((p) => (
              <button
                key={p.v}
                onClick={() => setProvider(p.v)}
                className={`rounded-lg border text-xs font-semibold py-2 px-3 text-left transition-colors ${
                  provider === p.v
                    ? "border-violet-400 bg-violet-50 text-violet-700"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`ai-banner-provider-${p.v}`}
              >
                <div>{p.label}</div>
                <div className="text-[10px] font-normal text-slate-500 mt-0.5">{p.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <Button
          onClick={generate}
          disabled={generating || !prompt.trim()}
          className="w-full h-11 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 text-white font-semibold shadow-md disabled:opacity-50"
          data-testid="ai-banner-generate-btn"
        >
          {generating
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</>
            : <><Sparkles className="h-4 w-4 mr-2" />Generate banner</>}
        </Button>
      </div>

      {/* Right column — preview + inspirations */}
      <div>
        {result?.url ? (
          <div className="space-y-4" data-testid="ai-banner-result">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div
                className="w-full bg-slate-50 rounded-xl overflow-hidden mx-auto"
                style={{
                  maxWidth: orientation === "vertical" ? 420 : (orientation === "square" ? 640 : 900),
                  aspectRatio: orientation === "vertical" ? "9/16" : (orientation === "square" ? "1/1" : "16/9"),
                }}
              >
                <img src={result.url} alt="Generated banner" className="w-full h-full object-cover" data-testid="ai-banner-result-img" />
              </div>
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                <Button
                  onClick={() => onOpenAsDesign(result.url)}
                  className="rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm h-10 px-5"
                  data-testid="ai-banner-open-editor"
                >
                  <ArrowRight className="h-4 w-4 mr-1.5" /> Open in editor
                </Button>
                <Button
                  variant="outline"
                  onClick={generate}
                  disabled={generating}
                  className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100 text-sm h-10 px-5"
                  data-testid="ai-banner-regenerate"
                >
                  <Sparkles className="h-4 w-4 mr-1.5" /> Regenerate
                </Button>
                <a href={result.url} target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100 text-sm h-10 px-5"
                    data-testid="ai-banner-open-full"
                  >
                    <ExternalLink className="h-4 w-4 mr-1.5" /> Open full-size
                  </Button>
                </a>
              </div>
            </div>
          </div>
        ) : generating ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 flex flex-col items-center justify-center text-center min-h-[420px]" data-testid="ai-banner-loading">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg animate-pulse mb-4">
              <Sparkles className="h-8 w-8" />
            </div>
            <div className="text-base font-bold text-slate-900" style={headingFont}>Painting your banner…</div>
            <div className="text-sm text-slate-500 mt-1">Usually takes about 15 seconds.</div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-6" data-testid="ai-banner-empty">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900" style={headingFont}>Try one of these to get started</h3>
                <p className="text-xs text-slate-500">Click any inspiration below or type your own on the left.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {inspirations.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(s.label)}
                  className="text-left rounded-xl border border-slate-200 bg-white hover:bg-violet-50 hover:border-violet-300 transition-colors p-3 flex items-start gap-3"
                  data-testid={`ai-inspiration-${i}`}
                >
                  <span className="text-2xl leading-none shrink-0">{s.emoji}</span>
                  <span className="text-xs text-slate-700 leading-snug">{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Tiny inline icons so we don't have to reach for another lucide-react import.
function UploadIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
  );
}
function Trash2Icon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
  );
}

